<template>
  <svg class="dial" viewBox="0 0 200 200" width="170" height="170" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <radialGradient :id="`${idPrefix}-face`" cx="50%" cy="45%" r="60%">
        <stop offset="0%" stop-color="#fbf3dc" />
        <stop offset="100%" stop-color="#e3d3a6" />
      </radialGradient>
      <linearGradient :id="`${idPrefix}-bezel`" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#e8c766" />
        <stop offset="50%" stop-color="#8a6a1f" />
        <stop offset="100%" stop-color="#d9b54f" />
      </linearGradient>
      <filter :id="`${idPrefix}-shadow`" x="-30%" y="-30%" width="160%" height="160%">
        <feDropShadow dx="0" dy="2" stdDeviation="1.5" flood-opacity="0.4" />
      </filter>
    </defs>

    <!-- Bezel and face -->
    <circle cx="100" cy="100" r="96" :fill="`url(#${idPrefix}-bezel)`" />
    <circle cx="100" cy="100" r="89" :fill="`url(#${idPrefix}-face)`" stroke="#5b4510" stroke-width="1" />

    <!-- Colored zones -->
    <path v-for="z in zoneArcs" :key="z.d" :d="z.d" fill="none" :stroke="z.color"
          stroke-width="4" stroke-opacity="0.75" />

    <!-- Scale arc -->
    <path :d="arc" fill="none" stroke="#3b2e10" stroke-width="1.2" />

    <!-- Ticks -->
    <g stroke="#3b2e10" stroke-linecap="round">
      <line v-for="t in ticks" :key="t.v"
            :x1="t.x1" :y1="t.y1" :x2="t.x2" :y2="t.y2" :stroke-width="t.w" />
    </g>

    <!-- Labels -->
    <g font-size="10" font-family="Georgia, 'Times New Roman', serif" font-weight="bold"
       text-anchor="middle" dominant-baseline="central" fill="#3b2e10">
      <text v-for="l in labels" :key="l.v" :x="l.x" :y="l.y">{{ l.v }}</text>
    </g>
    <text v-if="unit" x="100" y="150" font-size="11" font-family="Georgia, 'Times New Roman', serif"
          font-style="italic" text-anchor="middle" fill="#3b2e10">{{ unit }}</text>

    <!-- Needle -->
    <g class="needle" :style="`transform: rotate(${needleAngle}deg);`">
      <g :filter="`url(#${idPrefix}-shadow)`">
        <polygon points="100,26 104,100 100,112 96,100" fill="#8e1b1b" />
        <polygon points="100,26 100,112 96,100" fill="#fff" fill-opacity="0.2" />
      </g>
    </g>
    <circle cx="100" cy="100" r="7" :fill="`url(#${idPrefix}-bezel)`" stroke="#5b4510" stroke-width="1" />
    <circle cx="100" cy="100" r="2" fill="#3b2e10" />
  </svg>
</template>

<script>
const START = 190   // degrees clockwise from top: gap is centered at the bottom
const SPAN = 340

const CX = 100
const CY = 100

// angle (deg clockwise from top) -> point on circle of radius r
function polar(angle, r) {
  const rad = angle * Math.PI / 180
  return { x: CX + r * Math.sin(rad), y: CY - r * Math.cos(rad) }
}

function arcPath(a0, a1, r) {
  const p0 = polar(a0, r)
  const p1 = polar(a1, r)
  const large = a1 - a0 > 180 ? 1 : 0
  return `M ${p0.x} ${p0.y} A ${r} ${r} 0 ${large} 1 ${p1.x} ${p1.y}`
}

export default {
  name: 'DialGauge',
  props: {
    value: { type: Number, default: null },
    min: { type: Number, required: true },
    max: { type: Number, required: true },
    // colored scale zones: [from, to, color]
    zones: { type: Array, default: () => [] },
    unit: { type: String, default: '' },
    // must be unique per page: used for SVG gradient/filter ids
    idPrefix: { type: String, required: true },
    labelStep: { type: Number, default: 10 },
    labelRadius: { type: Number, default: 58 }
  },
  computed: {
    // needle rotation relative to its drawn position (pointing up)
    needleAngle() {
      const v = Number.isFinite(this.value) ? Math.min(this.max, Math.max(this.min, this.value)) : this.min
      return this.valueToAngle(v)
    },
    arc() {
      return arcPath(START, START + SPAN, 78)
    },
    zoneArcs() {
      return this.zones.map(([from, to, color]) => ({
        d: arcPath(this.valueToAngle(from), this.valueToAngle(to), 80),
        color
      }))
    },
    ticks() {
      const out = []
      for (let v = this.min; v <= this.max; v++) {
        const major = v % 10 === 0
        const mid = v % 5 === 0
        const len = major ? 10 : mid ? 7 : 4
        const a = this.valueToAngle(v)
        const p1 = polar(a, 78)
        const p2 = polar(a, 78 - len)
        out.push({ v, x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y, w: major ? 1.8 : mid ? 1.2 : 0.7 })
      }
      return out
    },
    labels() {
      const out = []
      const first = Math.ceil(this.min / this.labelStep) * this.labelStep
      for (let v = first; v <= this.max; v += this.labelStep) {
        const p = polar(this.valueToAngle(v), this.labelRadius)
        out.push({ v, x: p.x, y: p.y })
      }
      return out
    }
  },
  methods: {
    valueToAngle(v) {
      return START + (v - this.min) * SPAN / (this.max - this.min)
    }
  }
}
</script>

<style scoped>
.dial {
  display: block;
  margin: 10px auto 0;
}

.needle {
  transform-origin: 100px 100px;
  transition: transform 1.2s cubic-bezier(0.34, 1.3, 0.5, 1);
}
</style>
